import {
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useTenantTranslation } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import {
  deployPlanFormSlice,
  selectDeployFormMixOverride,
} from "src/store/slices/deployPlanFormSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

import { PlanId } from "../boundary/PlanId";

interface Props {
  planId: PlanId;
  period: Period;
  mixId: number;
  options: string[];
  disabled: boolean;
  required: boolean;
}

export const OverrideCell = ({
  planId,
  period,
  mixId,
  options,
  disabled,
  required,
}: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const override = useAppSelector((state) =>
    selectDeployFormMixOverride(state, planId, period, mixId)
  );

  const onChange = (event: SelectChangeEvent) => {
    dispatch(
      deployPlanFormSlice.actions.setMixOverride({
        planId,
        period,
        mixId,
        override: event.target.value,
      })
    );
  };

  const placeholder = required ? (
    <Tooltip title={t("pleaseSelectMixCodeOverride")}>
      <Chip
        label={t("missing")}
        size="small"
        color="error"
        variant="outlined"
      />
    </Tooltip>
  ) : (
    <Typography component="span" variant="body1Mono" color="text.disabled">
      {t("default")}
    </Typography>
  );

  return (
    <Select
      value={override}
      onChange={onChange}
      disabled={disabled}
      error={required && override === ""}
      displayEmpty
      variant="standard"
      fullWidth
      renderValue={(value) =>
        value === "" ? (
          placeholder
        ) : (
          <Typography component="span" variant="body1Mono">
            {value}
          </Typography>
        )
      }
      sx={{
        "&:before, &:after": { borderBottom: "none !important" },
        "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
        "& .MuiSelect-select": {
          padding: theme.spacing(0, 2.5, 0, 1),
          textAlign: "center",
        },
        "& .MuiSelect-icon": { color: theme.palette.text.disabled },
      }}
    >
      {required ? null : <MenuItem value="">{placeholder}</MenuItem>}
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};
